import { useState, useEffect } from "react";
import {C, withAlpha } from "../constants/theme";
import { fbList, fbAdd, fbUpdate, fbDelete, fbWatch } from "../hooks/useFirebase";
import { useConfirm } from "../hooks/useConfirm";
import { calcCustoPedido } from "../utils/calcCusto";
import { calcPagamento } from "../utils/pagamento";
import { valorCaixa, somarCaixa, dataCaixa } from "../utils/caixa";
import { calcFatura, faturaQuitada, saldoFatura, somarSaldos, somarPagos, distribuirPagamento, gerarParcelas } from "../utils/faturas";
import { normalizarStatus } from "../constants/statusPedido";
import { Card } from "./ui/Card";
import { Inp } from "./ui/Inp";
import { Sel } from "./ui/Sel";
import { Btn } from "./ui/Btn";
import { FaturasBancarias } from "./FaturasBancarias";
import "./financeiro.css";

export function Financeiro() {
  const [pedidos,     setPedidos]     = useState([]);
  const [filamentos,  setFilamentos]  = useState([]);
  const [impressoras, setImpressoras] = useState([]);
  const [materiais,   setMateriais]   = useState([]);
  const [produtos,    setProdutos]    = useState([]);
  const [faturas,     setFaturas]     = useState([]);
  const [caixa,       setCaixa]       = useState([]);
  const [vendas,      setVendas]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [tabFin,      setTabFin]      = useState("visao");
  const [confirmar,   modalConfirm]   = useConfirm();
  const [toast,       setToast]       = useState(null);
  const [modoFatura,  setModoFatura]  = useState("parcelas");
  const [filtroPdv,   setFiltroPdv]   = useState("dia");
  const [buscaPdv,    setBuscaPdv]    = useState("");

  const tiposFatura = ["Impressora","Manutenção","Energia","Material","Outro"];
  const emptyParc = { descricao:"", valorTotal:"", parcelas:"1", dataPrimeira:"", tipo:"Impressora", comprador:"", cartaoFinal:"" };
  const emptyAvulsa = { descricao:"", valor:"", vencimento:"", tipo:"Impressora", comprador:"", cartaoFinal:"" };
  const emptyCaixa = { descricao:"", valor:"", tipo:"Entrada" };
  const [formParc,   setFormParc]   = useState(emptyParc);
  const [formAvulsa, setFormAvulsa] = useState(emptyAvulsa);
  const [formCaixa,  setFormCaixa]  = useState(emptyCaixa);

  const showToast = (msg, cor=C.success) => {
    setToast({ msg, cor });
    setTimeout(() => setToast(null), 2800);
  };

  const carregar = async () => {
    setLoading(true);
    const [p,f,i,m,pr,fat,cx,v] = await Promise.all([
      fbList("pedidos"),fbList("filamentos"),fbList("impressoras"),
      fbList("materiais"),fbList("produtos"),fbList("faturas"),fbList("caixa"),fbList("vendas")
    ]);
    setPedidos(p); setFilamentos(f); setImpressoras(i);
    setMateriais(m); setProdutos(pr); setFaturas(fat); setCaixa(cx); setVendas(v);
    setLoading(false);
  };
  useEffect(() => {
    carregar();
    const unsubs = [
      fbWatch("pedidos", setPedidos),
      fbWatch("faturas", setFaturas),
      fbWatch("caixa", setCaixa),
      fbWatch("vendas", setVendas),
      fbWatch("filamentos", setFilamentos),
      fbWatch("materiais", setMateriais),
      fbWatch("produtos", setProdutos),
      fbWatch("impressoras", setImpressoras),
    ];
    return () => unsubs.forEach(u => u && u());
  }, []);

  const saveParcelamento = async () => {
    if (!formParc.descricao||!(parseFloat(formParc.valorTotal)>0)) { showToast("Preencha descrição e valor total!", C.warning); return; }
    if (!formParc.dataPrimeira) { showToast("Informe a data da 1ª parcela!", C.warning); return; }
    const parcelas = gerarParcelas(formParc.valorTotal, formParc.parcelas, formParc.dataPrimeira);
    const n = parcelas.length;
    const comprador = formParc.comprador?.trim() || null;
    const cartaoFinal = (formParc.cartaoFinal||"").replace(/\D/g,"").slice(-4) || null;
    await Promise.all(parcelas.map(p => fbAdd("faturas", {
      descricao: n>1 ? `${formParc.descricao} (${p.num}/${n})` : formParc.descricao,
      valor: p.valor,
      vencimento: p.vencimento,
      tipo: formParc.tipo,
      pago: false,
      parcela_grupo: formParc.descricao,
      parcela_num: p.num,
      parcela_total: n,
      comprador,
      cartao_final: cartaoFinal,
    })));
    setFormParc(emptyParc);
    showToast(n>1 ? `✅ ${n} parcelas criadas com sucesso!` : "✅ Fatura adicionada!");
  };

  const saveAvulsa = async () => {
    if (!formAvulsa.descricao||!(parseFloat(formAvulsa.valor)>0)) { showToast("Preencha descrição e valor!", C.warning); return; }
    await fbAdd("faturas", { descricao:formAvulsa.descricao, valor:parseFloat(formAvulsa.valor), vencimento:formAvulsa.vencimento||null, tipo:formAvulsa.tipo, pago:false,
      comprador: formAvulsa.comprador?.trim() || null,
      cartao_final: (formAvulsa.cartaoFinal||"").replace(/\D/g,"").slice(-4) || null });
    setFormAvulsa(emptyAvulsa);
    showToast("✅ Fatura adicionada!");
  };

  // Dupla confirmação: pergunta principal + confirmação final
  const confirmar2 = async (msg1, msg2) => (await confirmar(msg1)) && (await confirmar(msg2));

  // Um pagamento vira uma entrada no array da fatura; "pago" só é marcado
  // quando o valor fecha. Mesmo modelo dos pedidos (utils/pagamento.js).
  const registrarPagamento = (f, valor) => {
    const pagamentos = [...(Array.isArray(f.pagamentos) ? f.pagamentos : []),
      { id:`pg-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, valor, em:new Date().toISOString() }];
    return { pagamentos, ...(faturaQuitada({ ...f, pagamentos })
      ? { pago:true, data_pagamento:new Date().toLocaleDateString("pt-BR") } : {}) };
  };

  const pagarFatura = async (f) => {
    const saldo = saldoFatura(f);
    if (saldo <= 0) { showToast("Esta fatura já está quitada.", C.warning); return; }
    const valor = saldo.toFixed(2);
    const jaPago = calcFatura(f).pago;
    const rotulo = jaPago > 0 ? `Quitar "${f.descricao}"?\nFalta: R$ ${valor}` : `Marcar "${f.descricao}" como PAGO?\nValor: R$ ${valor}`;
    if (!await confirmar2(rotulo, `⚠️ Confirmar pagamento de R$ ${valor}?`)) return;
    const saldoAtual = somarCaixa(caixa);
    const usouCaixa = saldoAtual >= saldo;
    await fbUpdate("faturas", f.id, { ...registrarPagamento(f, saldo), pago_do_caixa:usouCaixa });
    if (usouCaixa) {
      await fbAdd("caixa", { descricao:`Pagamento: ${f.descricao}`, valor: -saldo, tipo:"Saída" });
    }
    showToast(usouCaixa ? `✅ Pago! Debitado R$ ${valor} do caixa.` : `✅ Marcado como pago!`);
  };

  // Pagamento no estilo fatura de cartão: um valor só, distribuído entre as
  // faturas em aberto da mais antiga para a mais nova (o vencido sai primeiro).
  const pagarValor = async (valor, lista = fatPendentes) => {
    const { alocacoes, usado, sobra } = distribuirPagamento(lista, valor);
    if (!alocacoes.length) { showToast("Informe um valor maior que zero.", C.warning); return false; }
    const alvo = alocacoes.length === 1 ? `"${alocacoes[0].fatura.descricao}"` : `${alocacoes.length} faturas`;
    if (!await confirmar2(`Pagar R$ ${usado.toFixed(2)} em ${alvo}?`,
      `⚠️ Confirmar pagamento de R$ ${usado.toFixed(2)}? Esta ação não pode ser desfeita.`)) return false;
    let saldoAtual = somarCaixa(caixa);
    let debitado = 0;
    const ops = [];
    for (const { fatura, valor:pago } of alocacoes) {
      const usouCaixa = saldoAtual >= pago;
      if (usouCaixa) { saldoAtual -= pago; debitado += pago; }
      ops.push(fbUpdate("faturas", fatura.id, { ...registrarPagamento(fatura, pago), pago_do_caixa:usouCaixa }));
      if (usouCaixa) ops.push(fbAdd("caixa", { descricao:`Pagamento: ${fatura.descricao}`, valor:-pago, tipo:"Saída" }));
    }
    await Promise.all(ops);
    const quitadas = alocacoes.filter(a=>a.quitada).length;
    showToast(`✅ R$ ${usado.toFixed(2)} pago${quitadas?` · ${quitadas} quitada(s)`:""}${debitado>0?` · R$ ${debitado.toFixed(2)} do caixa`:""}${sobra>0?` · sobraram R$ ${sobra.toFixed(2)}`:""}`);
    return true;
  };

  // Paga várias faturas pendentes de uma vez (grupo de parcelas ou mês inteiro)
  const pagarVarias = async (lista, rotulo) => {
    const pend = lista.filter(f => !faturaQuitada(f));
    if (pend.length === 0) { showToast("Nenhuma fatura pendente.", C.warning); return; }
    // Quita o que falta em cada uma: uma fatura já paga pela metade só recebe a
    // diferença, em vez de ser cobrada de novo pelo valor cheio.
    const total = somarSaldos(pend);
    if (!await confirmar2(`Pagar ${pend.length} fatura(s) de ${rotulo}?\nTotal: R$ ${total.toFixed(2)}`, `⚠️ Confirmar pagamento de R$ ${total.toFixed(2)}? Esta ação não pode ser desfeita.`)) return;
    let saldoAtual = somarCaixa(caixa);
    let debitado = 0;
    const ops = [];
    for (const f of pend) {
      const v = saldoFatura(f);
      const usouCaixa = saldoAtual >= v;
      if (usouCaixa) { saldoAtual -= v; debitado += v; }
      ops.push(fbUpdate("faturas", f.id, { ...registrarPagamento(f, v), pago_do_caixa:usouCaixa }));
      if (usouCaixa) ops.push(fbAdd("caixa", { descricao:`Pagamento: ${f.descricao}`, valor:-v, tipo:"Saída" }));
    }
    await Promise.all(ops);
    showToast(`✅ ${pend.length} fatura(s) paga(s)!${debitado>0?` Debitado R$ ${debitado.toFixed(2)} do caixa.`:""}`);
  };
  const pagarGrupo = (f) => pagarVarias(faturas.filter(fat => fat.parcela_grupo === f.parcela_grupo), `"${f.parcela_grupo}"`);
  const pagarMes = (chave, items) => pagarVarias(items, chave === "sem-data" ? "sem vencimento" : chave);

  const delFatura = async (id) => { if (await confirmar2("Excluir esta fatura?", "⚠️ Tem certeza? Esta ação não pode ser desfeita.")) { await fbDelete("faturas", id); } };

  const delGrupo = async (f) => {
    const grupo = f.parcela_grupo;
    const membros = faturas.filter(fat => fat.parcela_grupo === grupo);
    if (!await confirmar2(`Apagar TODAS as ${membros.length} parcelas de "${grupo}"?`, `⚠️ Confirmar exclusão de ${membros.length} parcelas? Não pode ser desfeito.`)) return;
    await Promise.all(membros.map(fat => fbDelete("faturas", fat.id)));
    showToast(`🗑 ${membros.length} parcelas removidas!`, C.danger);
  };

  const delMes = async (chave, items) => {
    const label = chave === "sem-data" ? "sem vencimento" : chave;
    if (!await confirmar2(`Apagar TODAS as ${items.length} faturas de ${label}?`, `⚠️ Confirmar exclusão de ${items.length} faturas? Não pode ser desfeito.`)) return;
    await Promise.all(items.map(f => fbDelete("faturas", f.id)));
    showToast(`🗑 ${items.length} faturas removidas!`, C.danger);
  };

  const saveCaixa = async () => {
    // O tipo escolhido decide o sinal: "-50" como saída não pode virar entrada.
    const valor = Math.abs(parseFloat(String(formCaixa.valor).replace(",", ".")) || 0);
    if (!formCaixa.descricao.trim()||!valor) { showToast("Preencha descrição e valor!", C.warning); return; }
    const saida = formCaixa.tipo==="Saída";
    await fbAdd("caixa", { descricao:formCaixa.descricao.trim(), valor: saida ? -valor : valor, tipo: saida ? "Saída" : "Entrada" });
    setFormCaixa(emptyCaixa);
    showToast(saida ? `💸 Saída de R$ ${valor.toFixed(2)} registrada!` : `💰 Entrada de R$ ${valor.toFixed(2)} registrada!`);
  };
  const delCaixa = async (id) => { if (await confirmar("Excluir movimento?")) { await fbDelete("caixa", id); } };

  const ativos     = pedidos.filter(p=>normalizarStatus(p.status)!=="cancelado");
  const concluidos = pedidos.filter(p=>["entregue","concluido","pronto_entrega"].includes(normalizarStatus(p.status)));
  const fatPedidos = ativos.reduce((s,p)=>s+calcPagamento(p).recebido,0); // dinheiro efetivamente recebido
  // Venda estornada não conta faturamento/custo — o dinheiro já voltou (ver
  // Caixa.jsx: estornarVenda marca .estornada em vez de apagar, pra manter
  // o registro no histórico mesmo excluído das somas). Venda fiado ainda não
  // foi paga: fica em "A Receber", não em "Recebido".
  const fiadoVendas = vendas.reduce((s,v)=>v.estornada||!v.fiado?s:s+(parseFloat(v.total)||0),0);
  const aReceber   = ativos.reduce((s,p)=>s+calcPagamento(p).saldo,0) + fiadoVendas; // saldo pendente dos clientes
  const fatVendas   = vendas.reduce((s,v)=>v.estornada||v.fiado?s:s+(parseFloat(v.total)||0),0);
  const faturamento = fatPedidos + fatVendas;
  const custoVendas = vendas.reduce((s,v)=>v.estornada?s:s+(v.itens||[]).reduce((si,it)=>si+(parseFloat(it.custo_unit)||0)*(it.qty||1),0),0);
  const custoPedidos = concluidos.reduce((s,p)=>s+calcCustoPedido(p,impressoras,produtos).total,0) + custoVendas;
  const lucro = faturamento - custoPedidos;
  // Parte do custo real que é da venda online (taxa da loja/cartão, frete e custos adicionais) — ver utils/vendaOnline.js
  const taxasVenda = concluidos.reduce((s,p)=>s+(parseFloat(p.venda_online?.custo_venda)||0),0);
  const investFilamentos  = filamentos.reduce((s,f)=>s+(parseFloat(f.custo)||0)*(parseFloat(f.peso_restante)||parseFloat(f.peso)||1000)/1000,0);
  const investMateriais   = materiais.reduce((s,m)=>s+(parseFloat(m.custo_unitario)||0)*(parseFloat(m.quantidade)||0),0);
  const investImpressoras = impressoras.reduce((s,i)=>s+(parseFloat(i.preco)||0),0);
  // Com pagamento parcial, "pendente" é o que ainda falta (não o valor cheio da
  // fatura) e "pago" inclui o que já entrou em faturas ainda abertas.
  const fatPendentes = faturas.filter(f=>!faturaQuitada(f));
  const totalPendente = somarSaldos(fatPendentes);
  const totalPago     = somarPagos(faturas);
  const saldoCaixa    = somarCaixa(caixa);

  const normPdv = s => (s||"").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g,"");
  const vendasPdv = (() => {
    const agora = new Date();
    const b = normPdv(buscaPdv);
    return [...vendas].filter(v => {
      try {
        const d = new Date(v.data);
        const noperiodo = filtroPdv==="dia" ? d.toDateString()===agora.toDateString()
          : filtroPdv==="semana" ? (agora-d)/86400000 < 7 && (agora-d) >= 0
          : d.getMonth()===agora.getMonth() && d.getFullYear()===agora.getFullYear();
        if (!noperiodo) return false;
        if (!b) return true;
        if (normPdv(v.cliente_nome||"").includes(b)) return true;
        return (v.itens||[]).some(it => normPdv(it.nome||"").includes(b));
      } catch { return false; }
    }).sort((a,b)=>new Date(b.data)-new Date(a.data));
  })();
  const totalPdv  = vendasPdv.reduce((s,v)=>v.estornada?s:s+(parseFloat(v.total)||0),0);
  const custoPdv  = vendasPdv.reduce((s,v)=>v.estornada?s:s+(v.itens||[]).reduce((si,it)=>si+(parseFloat(it.custo_unit)||0)*(it.qty||1),0),0);

  const previewParcelas = () => {
    if (!formParc.valorTotal) return [];
    return gerarParcelas(formParc.valorTotal, formParc.parcelas, formParc.dataPrimeira)
      .map(p => ({ num:p.num, val:p.valor, data:p.vencimento.split("-").reverse().join("/") }));
  };

  const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const anoAtual = new Date().getFullYear();
  const dadosMensais = meses.map((mes,mi) => {
    const pedMes = concluidos.filter(p => {
      try {
        let d;
        if (p.created_at?.toDate) d=p.created_at.toDate();
        else if (p.data) d=new Date(p.data.split("/").reverse().join("-")+"T00:00:00");
        else return false;
        return d.getMonth()===mi && d.getFullYear()===anoAtual;
      } catch { return false; }
    });
    const venMes = vendas.filter(v => {
      if (v.estornada) return false;
      try { const d=new Date(v.data); return d.getMonth()===mi && d.getFullYear()===anoAtual; }
      catch { return false; }
    });
    return { mes, fat: pedMes.reduce((s,p)=>s+(parseFloat(p.valor)||0),0) + venMes.reduce((s,v)=>s+(parseFloat(v.total)||0),0) };
  });
  const maxBar = Math.max(...dadosMensais.map(d=>d.fat), 1);

  const PieChart = ({ slices }) => {
    const total = slices.reduce((s,sl)=>s+sl.value,0);
    if (total===0) return <div style={{color:C.muted,textAlign:"center",padding:20,fontSize:13}}>Sem dados</div>;
    let angle=-Math.PI/2; const r=55,cx=70,cy=70;
    const paths=slices.map(sl=>{ const pct=sl.value/total; const a1=angle; const a2=angle+pct*2*Math.PI; angle=a2; const x1=cx+r*Math.cos(a1),y1=cy+r*Math.sin(a1),x2=cx+r*Math.cos(a2),y2=cy+r*Math.sin(a2); return {d:`M${cx},${cy} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 ${pct>.5?1:0},1 ${x2.toFixed(1)},${y2.toFixed(1)} Z`,color:sl.color,label:sl.label,value:sl.value,pct:(pct*100).toFixed(1)}; });
    return (
      <div style={{display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
        <svg width={140} height={140} viewBox="0 0 140 140" style={{flexShrink:0}}>
          {paths.map((p,i)=><path key={i} d={p.d} fill={p.color} stroke={C.bg} strokeWidth={2}/>)}
          <circle cx={cx} cy={cy} r={28} fill={C.card}/>
        </svg>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {paths.map((p,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:12}}>
              <div style={{width:10,height:10,borderRadius:2,background:p.color,flexShrink:0}}/>
              <span style={{color:C.muted,minWidth:80}}>{p.label}</span>
              <span style={{color:C.text,fontWeight:700}}>R$ {p.value.toFixed(2)}</span>
              <span style={{color:C.muted}}>({p.pct}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Faturas vencidas ou vencendo nos próximos 7 dias — vira badge na aba, pra
  // chamar atenção sem precisar entrar na aba Faturas pra descobrir.
  const em7dias = (() => { const d=new Date(); d.setDate(d.getDate()+7); return d.toISOString().slice(0,10); })();
  const faturasUrgentes = fatPendentes.filter(f => f.vencimento && f.vencimento <= em7dias).length;

  if (loading) return <div style={{color:C.muted,textAlign:"center",padding:40}}>⏳ Carregando...</div>;

  const prev = previewParcelas();

  return (
    <main className="finance-page">
      {modalConfirm}

      {toast && (
        <div style={{
          position:"fixed", top:20, right:20, zIndex:9998,
          background:toast.cor||C.success, color:"#fff",
          padding:"13px 22px", borderRadius:12,
          fontWeight:700, fontSize:14,
          boxShadow:"0 8px 32px rgba(0,0,0,0.4)",
          display:"flex", alignItems:"center", gap:8,
        }}>{toast.msg}</div>
      )}

      <section className="finance-hero" style={{
        background: saldoCaixa>=0
          ? "linear-gradient(135deg,rgba(46,213,115,0.12),rgba(0,212,170,0.08))"
          : "linear-gradient(135deg,rgba(255,71,87,0.12),rgba(255,71,87,0.06))",
        border:"1px solid "+(saldoCaixa>=0?withAlpha(C.success, "55"):withAlpha(C.danger, "55")),
        borderRadius:14, padding:"16px 22px",
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12,
      }}>
        <div className="finance-hero-copy">
          <div className="finance-eyebrow">VISÃO FINANCEIRA</div>
          <h1>Financeiro</h1>
          <div style={{color:C.muted,fontSize:11,letterSpacing:1,marginBottom:4}}>SALDO DISPONÍVEL NO CAIXA</div>
          <div style={{color:saldoCaixa>=0?C.success:C.danger,fontWeight:900,fontSize:28}}>
            R$ {saldoCaixa.toFixed(2)}
          </div>
          {totalPendente>0&&<div style={{color:C.muted,fontSize:11,marginTop:4}}>
            {saldoCaixa>=totalPendente
              ? `✅ Cobre todas as faturas pendentes (R$ ${totalPendente.toFixed(2)})`
              : `⚠️ Falta R$ ${(totalPendente-saldoCaixa).toFixed(2)} para cobrir as pendências`}
          </div>}
        </div>
        <div className="finance-hero-actions">
          <Btn color={C.success} onClick={()=>{ setFormCaixa(f => ({ ...f, tipo:"Entrada" })); setTabFin("caixa"); }} style={{minHeight:44,padding:"0 20px"}}>Registrar entrada</Btn>
          <Btn outline color={C.danger} onClick={()=>{ setFormCaixa(f => ({ ...f, tipo:"Saída" })); setTabFin("caixa"); }} style={{minHeight:44,padding:"0 20px"}}>Registrar saída</Btn>
        </div>
      </section>

      <nav className="finance-tabs" aria-label="Seções financeiras">
        {[
          ["visao","📊","Visão Geral"],
          ["faturas","🧾","Faturas", faturasUrgentes>0?faturasUrgentes:null],
          ["caixa","💼","Caixa"],
          ["custos","💰","Custos"],
          ["pdv","🛒","PDV"],
        ].map(([id,icon,l,badge])=>{
          const ativo = tabFin===id;
          return (
            <button key={id} onClick={()=>setTabFin(id)}
              style={{
                display:"flex", alignItems:"center", gap:7, flexShrink:0,
                minHeight:44, padding:"0 16px", borderRadius:12,
                background: ativo ? withAlpha(C.success, "1c") : C.card,
                border:"1.5px solid "+(ativo?C.success:C.border),
                color: ativo ? C.success : C.muted,
                fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap",
                transition:"all .15s",
              }}>
              <span style={{fontSize:16}}>{icon}</span>
              <span>{l}</span>
              {badge && (
                <span style={{background:C.danger, color:"#fff", borderRadius:10, minWidth:18, height:18, fontSize:10, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px"}}>{badge}</span>
              )}
            </button>
          );
        })}
      </nav>

      {tabFin==="visao" && <>
        <section className="finance-metrics">
          {[
            {l:"💵 Recebido",      v:faturamento,   c:C.success},
            {l:"💳 A Receber",     v:aReceber,      c:C.accent},
            {l:"📦 Custo Real",    v:custoPedidos,  c:C.danger},
            {l:"📈 Lucro Líquido", v:lucro,         c:lucro>=0?C.accent2:C.danger},
            {l:"💼 Saldo Caixa",   v:saldoCaixa,    c:saldoCaixa>=0?C.success:C.danger},
            {l:"🧾 Fat. Pendentes",v:totalPendente, c:C.warning},
          ].map(x=>(
            <Card key={x.l} className="finance-metric" style={{textAlign:"center",padding:16}}>
              <div style={{color:C.muted,fontSize:10,letterSpacing:1,marginBottom:6}}>{x.l}</div>
              <div style={{color:x.c,fontWeight:900,fontSize:20}}>R$ {x.v.toFixed(2)}</div>
            </Card>
          ))}
        </section>

        <Card className="finance-panel finance-chart">
          <h3 style={{color:C.success,margin:"0 0 16px",fontSize:13,letterSpacing:1}}>📈 FATURAMENTO MENSAL {anoAtual}</h3>
          <svg width="100%" viewBox="0 0 520 130" style={{overflow:"visible"}}>
            <defs>
              <linearGradient id="bg1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.accent2}/><stop offset="100%" stopColor="var(--pc-ui-0ea5e9, #0ea5e9)"/>
              </linearGradient>
            </defs>
            {dadosMensais.map((d,i)=>{
              const W=520,pad=20,colW=(W-pad*2)/12;
              const barH=d.fat>0?(d.fat/maxBar)*90:0;
              const x=pad+i*colW+colW*.1,bw=colW*.8;
              return (
                <g key={i}>
                  <rect x={x} y={10} width={bw} height={90} fill={C.panel} rx={4}/>
                  <rect x={x} y={10+(90-barH)} width={bw} height={barH||1} fill={barH>0?"url(#bg1)":C.border} rx={4}/>
                  <text x={x+bw/2} y={118} textAnchor="middle" fill={C.muted} fontSize={8}>{d.mes}</text>
                  {barH>10&&<text x={x+bw/2} y={10+(90-barH)-3} textAnchor="middle" fill={C.accent2} fontSize={7}>R${d.fat.toFixed(0)}</text>}
                </g>
              );
            })}
          </svg>
        </Card>

        <section className="finance-overview-grid">
          <Card className="finance-panel">
            <h3 style={{color:C.success,margin:"0 0 12px",fontSize:13}}>📊 DISTRIBUIÇÃO DE INVESTIMENTO</h3>
            <PieChart slices={[
              {label:"Filamentos",  value:investFilamentos,  color:C.accent2},
              {label:"Materiais",   value:investMateriais,   color:"#e879f9"},
              {label:"Impressoras", value:investImpressoras, color:C.accent3},
              {label:"Fat. Pagas",  value:totalPago,         color:C.warning},
            ].filter(s=>s.value>0)} />
          </Card>
          <Card className="finance-panel">
            <h3 style={{color:C.success,margin:"0 0 12px",fontSize:13}}>🏦 RESUMO GERAL</h3>
            {[
              ["🧵 Invest. Filamentos", investFilamentos,  C.accent2],
              ["🔧 Invest. Materiais",  investMateriais,   "#e879f9"],
              ["🖨️ Invest. Impressoras",investImpressoras, C.accent3],
              ["🧾 Fat. Pendentes",     totalPendente,     C.warning],
              ["✅ Fat. Pagas",         totalPago,         C.muted],
              ["💼 Saldo Caixa",        saldoCaixa,        saldoCaixa>=0?C.success:C.danger],
              ["💵 Recebido",           faturamento,       C.success],
              ["💳 A Receber",          aReceber,          C.accent],
              ...(taxasVenda>0 ? [["🛒 Taxas e custos de venda", taxasVenda, C.warning]] : []),
              ["📈 Lucro",             lucro,             lucro>=0?C.accent2:C.danger],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+C.border,fontSize:12}}>
                <span style={{color:C.muted}}>{l}</span>
                <span style={{color:c,fontWeight:700}}>R$ {v.toFixed(2)}</span>
              </div>
            ))}
          </Card>
        </section>
      </>}

      {tabFin==="faturas" && <FaturasBancarias
        faturas={faturas} saldoCaixa={saldoCaixa}
        totalPendente={totalPendente} totalPago={totalPago}
        pagarFatura={pagarFatura} delFatura={delFatura} delGrupo={delGrupo} delMes={delMes}
        pagarGrupo={pagarGrupo} pagarMes={pagarMes} pagarValor={pagarValor}
        formParc={formParc} setFormParc={setFormParc}
        formAvulsa={formAvulsa} setFormAvulsa={setFormAvulsa}
        modoFatura={modoFatura} setModoFatura={setModoFatura}
        tiposFatura={tiposFatura}
        saveParcelamento={saveParcelamento} saveAvulsa={saveAvulsa}
        prev={prev}
      />}

      {tabFin==="caixa" && <>
        <Card className="finance-panel finance-cash-form">
          <h3 style={{color:C.success,margin:"0 0 16px",fontSize:15,letterSpacing:1}}>💼 REGISTRAR MOVIMENTO</h3>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div style={{gridColumn:"1/-1"}}>
              <Inp label="DESCRIÇÃO" value={formCaixa.descricao} onChange={e=>setFormCaixa({...formCaixa,descricao:e.target.value})} placeholder="Ex: Venda Ender 3, Recebimento cliente..." />
            </div>
            <Inp label="VALOR (R$)" type="number" value={formCaixa.valor} onChange={e=>setFormCaixa({...formCaixa,valor:e.target.value})} placeholder="Ex: 1500.00" />
            <Sel label="TIPO" value={formCaixa.tipo} onChange={e=>setFormCaixa({...formCaixa,tipo:e.target.value})}>
              <option>Entrada</option>
              <option>Saída</option>
            </Sel>
          </div>
          <div style={{marginTop:14,display:"flex",gap:10}}>
            <Btn color={formCaixa.tipo==="Entrada"?C.success:C.danger} onClick={saveCaixa}>
              {formCaixa.tipo==="Entrada"?"💰 Registrar Entrada":"💸 Registrar Saída"}
            </Btn>
          </div>
        </Card>

        <Card className="finance-panel finance-statement">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <h3 style={{color:C.accent2,margin:0,fontSize:14}}>📋 EXTRATO DO CAIXA</h3>
            <div style={{textAlign:"right"}}>
              <div style={{color:C.muted,fontSize:10}}>SALDO ATUAL</div>
              <div style={{color:saldoCaixa>=0?C.success:C.danger,fontWeight:900,fontSize:20}}>R$ {saldoCaixa.toFixed(2)}</div>
            </div>
          </div>
          {caixa.length===0&&<div style={{color:C.muted,fontSize:13,textAlign:"center",padding:20}}>Nenhum movimento registrado.</div>}
          {[...caixa].sort((a,b)=>dataCaixa(b)-dataCaixa(a)).map(c=>{
            const valor = valorCaixa(c);
            const entrada = valor>=0;
            return (
              <div key={c.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid "+C.border,gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flex:1}}>
                  <div style={{width:32,height:32,borderRadius:8,background:entrada?withAlpha(C.success, "22"):withAlpha(C.danger, "22"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                    {entrada?"💰":"💸"}
                  </div>
                  <div>
                    <div style={{color:C.text,fontSize:13,fontWeight:600}}>{c.descricao}</div>
                    <div style={{color:C.muted,fontSize:11}}>{c.tipo}</div>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{color:entrada?C.success:C.danger,fontWeight:900,fontSize:15}}>
                    {entrada?"+":""}{valor.toFixed(2)}
                  </span>
                  <Btn small danger onClick={()=>delCaixa(c.id)}>🗑️</Btn>
                </div>
              </div>
            );
          })}
        </Card>
      </>}

      {tabFin==="custos" && (
        <section className="finance-cost-grid">
          <Card className="finance-panel">
            <h3 style={{color:C.accent2,margin:"0 0 12px",fontSize:13,letterSpacing:1}}>🧵 FILAMENTOS</h3>
            {filamentos.length===0&&<div style={{color:C.muted,fontSize:12}}>Nenhum filamento</div>}
            {filamentos.map(f=>(
              <div key={f.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+C.border,fontSize:12}}>
                <div><span style={{color:C.text}}>{f.cor||f.nome}</span><span style={{color:C.muted,marginLeft:6}}>{f.material} {f.peso||1000}g</span></div>
                <span style={{color:C.accent2,fontWeight:700}}>R$ {((parseFloat(f.custo)||0)*(parseFloat(f.peso)||1000)/1000).toFixed(2)}</span>
              </div>
            ))}
            <div style={{marginTop:10,textAlign:"right",color:C.accent2,fontWeight:900}}>Total: R$ {investFilamentos.toFixed(2)}</div>
          </Card>
          <Card className="finance-panel">
            <h3 style={{color:"#e879f9",margin:"0 0 12px",fontSize:13,letterSpacing:1}}>🔧 MATERIAIS</h3>
            {materiais.length===0&&<div style={{color:C.muted,fontSize:12}}>Nenhum material</div>}
            {materiais.map(m=>(
              <div key={m.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+C.border,fontSize:12}}>
                <div><span style={{color:C.text}}>{m.nome}</span><span style={{color:C.muted,marginLeft:6}}>{m.quantidade} {m.unidade}</span></div>
                <span style={{color:"#e879f9",fontWeight:700}}>R$ {((parseFloat(m.custo_unitario)||0)*(parseFloat(m.quantidade)||0)).toFixed(2)}</span>
              </div>
            ))}
            <div style={{marginTop:10,textAlign:"right",color:"#e879f9",fontWeight:900}}>Total: R$ {investMateriais.toFixed(2)}</div>
          </Card>
          <Card className="finance-panel">
            <h3 style={{color:C.accent3,margin:"0 0 12px",fontSize:13,letterSpacing:1}}>🖨️ IMPRESSORAS</h3>
            {impressoras.length===0&&<div style={{color:C.muted,fontSize:12}}>Nenhuma impressora</div>}
            {impressoras.map(i=>(
              <div key={i.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid "+C.border,fontSize:12}}>
                <span style={{color:C.text}}>{i.nome}</span>
                <span style={{color:i.preco?C.accent3:C.muted,fontWeight:700}}>{i.preco?"R$ "+parseFloat(i.preco).toFixed(2):"—"}</span>
              </div>
            ))}
            <div style={{marginTop:10,textAlign:"right",color:C.accent3,fontWeight:900}}>Total: R$ {investImpressoras.toFixed(2)}</div>
          </Card>
          <Card className="finance-panel">
            <h3 style={{color:C.warning,margin:"0 0 12px",fontSize:13,letterSpacing:1}}>🧾 FATURAS + CAIXA</h3>
            {[
              ["⏳ Pendentes",    totalPendente,              C.warning],
              ["✅ Pagas",        totalPago,                  C.success],
              ["💼 Saldo Caixa",  saldoCaixa,                 saldoCaixa>=0?C.success:C.danger],
              ["📊 Total Faturas",totalPendente+totalPago,    C.text],
            ].map(([l,v,c])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid "+C.border,fontSize:13}}>
                <span style={{color:C.muted}}>{l}</span>
                <span style={{color:c,fontWeight:700}}>R$ {v.toFixed(2)}</span>
              </div>
            ))}
            <div style={{marginTop:16,padding:12,background:C.panel,borderRadius:8}}>
              <div style={{color:C.muted,fontSize:10,marginBottom:8}}>Capital imobilizado no negócio</div>
              {[
                ["🧵 Filamentos em estoque", investFilamentos,  C.accent2],
                ["🔧 Materiais em estoque",  investMateriais,   "#e879f9"],
                ["🖨️ Impressoras",           investImpressoras, C.accent3],
              ].map(([l,v,c])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"3px 0"}}>
                  <span style={{color:C.muted}}>{l}</span>
                  <span style={{color:c,fontWeight:700}}>R$ {v.toFixed(2)}</span>
                </div>
              ))}
              <div style={{borderTop:"1px solid "+C.border,marginTop:8,paddingTop:8,display:"flex",justifyContent:"space-between"}}>
                <span style={{color:C.muted,fontSize:11,fontWeight:700}}>💼 TOTAL INVESTIDO</span>
                <span style={{color:C.text,fontWeight:900,fontSize:20}}>R$ {(investFilamentos+investMateriais+investImpressoras).toFixed(2)}</span>
              </div>
              <div style={{marginTop:10,borderTop:"1px solid "+C.border,paddingTop:8}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                  <span style={{color:C.muted}}>✅ Parcelas pagas</span>
                  <span style={{color:C.success,fontWeight:700}}>R$ {totalPago.toFixed(2)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                  <span style={{color:C.muted}}>⏳ Parcelas a pagar</span>
                  <span style={{color:C.warning,fontWeight:700}}>R$ {totalPendente.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </Card>
        </section>
      )}

      {tabFin==="pdv" && <>
        <div className="finance-pdv-toolbar">
          {[["dia","Hoje"],["semana","Semana"],["mes","Mês"]].map(([k,l])=>(
            <button key={k} onClick={()=>setFiltroPdv(k)}
              style={{padding:"7px 20px",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer",
                background:filtroPdv===k?"rgba(0,212,170,0.12)":"none",
                border:"1px solid "+(filtroPdv===k?C.accent2:C.border),
                color:filtroPdv===k?C.accent2:C.muted}}>
              {l}
            </button>
          ))}
          <div style={{position:"relative",flex:1,minWidth:180}}>
            <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:13,pointerEvents:"none"}}>🔍</span>
            <input value={buscaPdv} onChange={e=>setBuscaPdv(e.target.value)} placeholder="Buscar produto ou cliente..."
              style={{width:"100%",boxSizing:"border-box",background:C.card,border:"1px solid "+C.border,borderRadius:10,color:C.text,padding:"7px 10px 7px 32px",fontSize:13,outline:"none"}} />
            {buscaPdv&&<button onClick={()=>setBuscaPdv("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:14,lineHeight:1,padding:0}}>✕</button>}
          </div>
        </div>

        <section className="finance-metrics finance-pdv-metrics">
          {[
            {l:"🛒 Vendas",       v:vendasPdv.length,           c:C.text,    fmt:false},
            {l:"💰 Faturamento",  v:totalPdv,                   c:C.success, fmt:true},
            {l:"📦 Custo",        v:custoPdv,                   c:C.danger,  fmt:true},
            {l:"📈 Lucro",        v:totalPdv-custoPdv,          c:(totalPdv-custoPdv)>=0?C.accent2:C.danger, fmt:true},
          ].map(x=>(
            <Card key={x.l} className="finance-metric" style={{textAlign:"center",padding:16}}>
              <div style={{color:C.muted,fontSize:10,letterSpacing:1,marginBottom:6}}>{x.l}</div>
              <div style={{color:x.c,fontWeight:900,fontSize:20}}>{x.fmt?`R$ ${x.v.toFixed(2)}`:x.v}</div>
            </Card>
          ))}
        </section>

        {vendasPdv.length===0 ? (
          <Card className="finance-panel"><div style={{color:C.muted,textAlign:"center",padding:32,fontSize:13}}>Nenhuma venda no período</div></Card>
        ) : (
          <Card className="finance-panel finance-sales-list">
            <h3 style={{color:C.accent2,margin:"0 0 16px",fontSize:13,letterSpacing:1}}>🛒 VENDAS PDV</h3>
            {vendasPdv.map(v=>{
              const d = new Date(v.data);
              const hora = d.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
              const data = d.toLocaleDateString("pt-BR",{day:"2-digit",month:"2-digit"});
              const pagIcon = {dinheiro:"💵",pix:"📱",cartao:"💳"}[v.formaPagamento]||"💳";
              const pagLabel = {dinheiro:"Dinheiro",pix:"PIX",cartao:"Cartão"}[v.formaPagamento]||v.formaPagamento;
              return (
                <div key={v.id} style={{borderBottom:"1px solid "+C.border,paddingBottom:14,marginBottom:14,opacity:v.estornada?0.55:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:11,color:C.muted,fontWeight:600}}>{filtroPdv==="dia"?hora:`${data} ${hora}`}</span>
                      <span style={{fontSize:12}}>{pagIcon}</span>
                      <span style={{fontSize:11,color:C.muted}}>{pagLabel}</span>
                      {v.fiado&&<span style={{fontSize:10,color:C.danger,fontWeight:800,background:"rgba(239,68,68,0.1)",borderRadius:4,padding:"1px 6px"}}>FIADO</span>}
                      {v.estornada&&<span style={{fontSize:10,color:C.danger,fontWeight:800,background:"rgba(239,68,68,0.1)",borderRadius:4,padding:"1px 6px"}}>ESTORNADA</span>}
                      {v.cliente_nome&&<span style={{fontSize:11,color:C.muted}}>· {v.cliente_nome}</span>}
                    </div>
                    <span style={{fontWeight:900,color:C.success,fontSize:15,textDecoration:v.estornada?"line-through":"none"}}>R$ {parseFloat(v.total).toFixed(2)}</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:3,paddingLeft:8}}>
                    {(v.itens||[]).map((it,idx)=>(
                      <div key={idx} style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                        <span style={{color:C.text}}>{it.nome} <span style={{color:C.muted}}>×{it.qty}</span></span>
                        <span style={{color:C.muted}}>R$ {parseFloat(it.subtotal||it.preco*it.qty).toFixed(2)}</span>
                      </div>
                    ))}
                    {parseFloat(v.desconto)>0&&(
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.danger}}>
                        <span>Desconto</span><span>-R$ {parseFloat(v.desconto).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        )}
      </>}
    </main>
  );
}
